import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Stack,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { useIsMobile } from "../../../../hooks/state-checkers/use-is-mobile";
import { getCurrentDate } from "../../../../helpers";
import { useReportStore } from "../../../../store/reportStore";
import { useReportForm } from "../hooks/useReportForm";
import { CategoryStep } from "../steps/CategoryStep";
import { SubcategoryStep } from "../steps/SubcategoryStep";
import { DescriptionStep } from "../steps/DescriptionStep";
import { SummaryStep } from "../steps/SummaryStep";
import { SuccessStep } from "../steps/SuccessStep";
import { REPORT_CATEGORIES } from "../constants/reportCategories";
import { STEPS } from "../constants/reportSteps";

export const ReportDialog = () => {
  const { reportState } = useReportStore();
  const { isReportOpen } = reportState;
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);

  const {
    control,
    handleSubmit,
    watch,
    errors,
    watchedCategory,
    watchedSubcategory,
    watchedDescription,
    submitError,
    isSubmittingReport,
    onSubmit,
    handleCancel,
  } = useReportForm(setActiveStep);

  // Get current category data
  const currentCategory = REPORT_CATEGORIES.find(cat => cat.id === watchedCategory);
  const hasSubcategories = currentCategory?.subcategories?.length > 0;

  // Step navigation
  const handleNext = () => {
    if (activeStep === 0 && watchedCategory) {
      if (hasSubcategories) {
        setActiveStep(1);
      } else {
        setActiveStep(2); // Skip subcategory step
      }
    } else if (activeStep === 1 && (watchedSubcategory || !hasSubcategories)) {
      setActiveStep(2);
    } else if (activeStep === 2) {
      setActiveStep(3); // Go to summary step
    } else if (activeStep === 3) {
      setActiveStep(4); // Go to success step
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  // Wrapper for onSubmit that also advances the step
  const handleSubmitAndAdvance = async (data) => {
    try {
      await onSubmit(data);
      // Only advance if submission was successful (no error thrown)
      handleNext();
    } catch (error) {
      // Error is already handled in onSubmit, just don't advance
    }
  };

  // Render step content
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return <CategoryStep control={control} errors={errors} />;
      case 1:
        return (
          <SubcategoryStep 
            control={control} 
            errors={errors} 
            selectedCategory={watchedCategory}
          />
        );
      case 2:
        return <DescriptionStep control={control} errors={errors} watch={watch} />;
      case 3:
        return (
          <SummaryStep 
            watchedCategory={watchedCategory}
            watchedSubcategory={watchedSubcategory}
            watchedDescription={watchedDescription}
          />
        );
      case 4:
        return <SuccessStep />;
      default:
        return null;
    }
  };

  // Determine if next button should be enabled
  const isNextEnabled = () => {
    switch (activeStep) {
      case 0:
        return !!watchedCategory;
      case 1:
        return !!watchedSubcategory || !hasSubcategories;
      case 2:
        return !!watchedDescription && watchedDescription.length >= 10;
      case 3:
        return true; // Summary step - always enabled
      default:
        return false;
    }
  };

  if (!isReportOpen) return null;

  return (
    <Dialog
      open={isReportOpen}
      onClose={handleCancel}
      fullWidth
      fullScreen={isMobile}
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          minHeight: isMobile ? '100vh' : 'auto',
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="h1">
          <FormattedMessage id="general.report" defaultMessage="Melden" />
        </Typography>
        {activeStep < 4 && (
          <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
            {STEPS.map((step, index) => (
              <Step key={step.id}>
                <StepLabel>
                  <FormattedMessage id={step.label} defaultMessage={step.label} />
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </DialogTitle>

      <DialogContent sx={{ minHeight: 400 }}>
        <Stack spacing={3}>
          {activeStep < 4 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {activeStep === 0 && <FormattedMessage id="general.choose-category" defaultMessage="Wählen Sie eine Kategorie" />}
                {activeStep === 1 && (
                  <>
                    <FormattedMessage id="general.subcategory-for" defaultMessage="Unterkategorie für:" /> {currentCategory?.name}
                  </>
                )}
                {activeStep === 2 && <FormattedMessage id="general.add-description" defaultMessage="Fügen Sie eine Beschreibung hinzu" />}
                {activeStep === 3 && <FormattedMessage id="general.review-information" defaultMessage="Überprüfen Sie Ihre Angaben" />}
              </Typography>
              {activeStep === 2 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <FormattedMessage id="general.date" defaultMessage="Datum" />: {getCurrentDate()}
                </Typography>
              )}
            </Box>
          )}

          {renderStepContent(activeStep)}

          {submitError && (
            <Alert severity="error">
              {submitError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            size="large"
          >
            {activeStep === 4 ? (
              <FormattedMessage id="general.cancel" defaultMessage="Schließen" />
            ) : (
              <FormattedMessage id="general.abort" defaultMessage="Abbrechen" />
            )}
          </Button>
          
          {activeStep < 4 && (
            <>
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  variant="outlined"
                  size="large"
                >
                  <FormattedMessage id="general.back" defaultMessage="Zurück" />
                </Button>
              )}
              
              <Button
                onClick={activeStep === 3 ? handleSubmit(handleSubmitAndAdvance) : handleNext}
                variant="contained"
                size="large"
                disabled={!isNextEnabled() || isSubmittingReport}
                sx={{ ml: 'auto' }}
              >
                {isSubmittingReport ? (
                  <CircularProgress size={20} />
                ) : activeStep === 3 ? (
                  <FormattedMessage id="general.submit" defaultMessage="Absenden" />
                ) : (
                  <FormattedMessage id="general.next" defaultMessage="Weiter" />
                )}
              </Button>
            </>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
